import { useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import firebaseFirestore from '~/config/firebase/firestore';
import { addDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { Company } from '~/core/types';
import useGetParam from '~/hooks/useGetParam';
import { Center, Link, Spinner, Text, VStack, Box } from '@chakra-ui/react';
import Form from './Reservation/Form';
import { Helmet } from 'react-helmet-async';
import { evaluarAcceso } from '~/utils/embedOrigin';
import { useAuth } from '~/hooks/useAuth';
import { useTranslation } from 'react-i18next';

export const Forms = () => {
  const [isLoading, setIsLoading] = useState(false);
  const params = useParams();
  const externalId = useGetParam('externalId');
  const from = useGetParam('from');
  const lang = useGetParam('lang');
  const code = useGetParam('code');
  const gclidParam = useGetParam('gclid');
  const source = useGetParam('source');

  const [company, setCompany] = useState<Company>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { t } = useTranslation();

  // Check if company is active
  const isCompanyInactive = company?.status === 'inactive';

  // ¿Se está abriendo el formulario desde donde debe? Solo bloquea si la
  // empresa lo tiene configurado en modo 'enforce'; sin configuración, o en
  // modo observación, se permite igual que siempre.
  const acceso = useMemo(() => {
    if (!company) return null;
    return evaluarAcceso({
      modo: company.security?.mode ?? 'off',
      dominiosPermitidos: company.security?.allowedDomains ?? [],
      conSesion: Boolean(user?.uid),
      conCodigo: Boolean(code || params.code),
    });
  }, [company, user?.uid, code, params.code]);

  // Deja constancia de por dónde entró cada visita, para poder revisarlo antes
  // de activar el bloqueo. Nunca interrumpe el flujo si falla.
  useEffect(() => {
    if (!company || !acceso) return;
    if ((company.security?.mode ?? 'off') === 'off') return;

    addDoc(collection(firebaseFirestore, 'activity_logs'), {
      activityType: 'form_access',
      entityType: 'company',
      entityId: company.id,
      companyId: company.id,
      userId: user?.uid ?? null,
      userEmail: user?.email ?? null,
      timestamp: new Date(),
      details: {
        permitido: acceso.permitido,
        motivo: acceso.motivo,
        dominioPadre: acceso.origen.dominioPadre,
        enIframe: acceso.origen.enIframe,
        fuente: acceso.origen.fuente,
        referrer: acceso.origen.referrer,
        url: window.location.href,
        userAgent: navigator.userAgent,
      },
    }).catch(() => {
      /* el registro nunca debe impedir reservar */
    });
  }, [company?.id, acceso?.motivo]);

  // Function to get cookie value
  const getCookie = (name: string): string | null => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
    return null;
  };

  const getCompany = async () => {
    const collectionRef = collection(firebaseFirestore, 'companies');
    const q = query(collectionRef, where('externalId', '==', params.externalId ?? externalId));

    const querySnapshot = await getDocs(q);
    if (querySnapshot.docs.length > 0) {
      const doc = querySnapshot.docs[0];
      setCompany({
        id: doc.id,
        ...doc.data(),
      } as Company);
    }
  };
  useEffect(() => {
    if (company) {
      if (!externalId) {
        const gclid = getCookie('_gcl_aw') || gclidParam;
        setSearchParams({
          company: company?.id ?? '',
          ...(code ? { code } : {}),
          ...(from ? { from } : {}),
          ...(lang ? { lang } : {}),
          ...(gclid ? { gclid } : {}),
          ...(source ? { source } : {}),
          ...Object.fromEntries(searchParams.entries()),
        });
      }
      setIsLoading(false);
    }
  }, [company]);

  useEffect(() => {
    if (params.externalId || externalId) {
      setIsLoading(true);
      getCompany();
    }
  }, [params.externalId, externalId]);

  if (isLoading) {
    return (
      <Box
        display='flex'
        justifyContent={{ base: 'center', md: 'center' }}
        alignItems={{ md: 'center' }}
        mt={{ base: '320px', md: '0' }}
        minH='100vh'
      >
        <Spinner size={'lg'} />
      </Box>
    );
  }

  // Get company Google Ads data if available, or use fallback values
  const conversionId = company?.settings?.integrations?.googleAds?.conversionId || 'AW-437148397';
  const conversionTag =
    company?.settings?.integrations?.googleAds?.conversionTag || 'cP08COGm-LYaEO21udAB';

  // Get company Meta Ads data if available, or use fallback values
  const metaPixelId = company?.settings?.integrations?.metaAds?.pixelId || '1731663031561672';
  console.log('metaPixelId', metaPixelId);
  // El formulario se está abriendo desde un sitio no autorizado y la empresa
  // tiene el bloqueo activado. En la práctica quien llega aquí casi siempre es
  // alguien que abrió el enlace de su correo y recargó la página: al recargar,
  // la aplicación reescribe la URL y se pierde el código de la reserva, así que
  // el mensaje explica cómo volver a entrar en lugar de dejarle sin salida.
  //
  // El idioma se resuelve solo para esta pantalla con la opción `lng`, sin
  // tocar el idioma global: aquí no llega a montarse el formulario, que es
  // quien normalmente lo fija.
  if (company && acceso && !acceso.permitido) {
    const idioma = lang === 'en' ? 'en' : 'es';
    const sitio = company.security?.allowedDomains?.[0];
    return (
      <Center h={'100vh'}>
        <VStack spacing={4} textAlign='center' maxW='460px' px={4}>
          <Text fontSize='xl' fontWeight='bold'>
            {t('form_access_blocked.title', { lng: idioma })}
          </Text>
          <Text color='gray.600'>
            {t('form_access_blocked.description', { lng: idioma, company: company.name })}
          </Text>
          <Text color='gray.500' fontSize='sm'>
            {t('form_access_blocked.why', { lng: idioma })}
          </Text>
          {sitio && (
            <Text color='gray.500' fontSize='sm'>
              {t('form_access_blocked.new_booking', { lng: idioma })}{' '}
              <Link isExternal href={`https://${sitio}`} color='blue.500'>
                {sitio}
              </Link>
            </Text>
          )}
          <Box mt={8}>
            <Text fontSize={'sm'} textAlign={'center'}>
              Desarrollado por
              <Link isExternal href='https://sontickets.com/'>
                {' '}
                sontickets.com
              </Link>
            </Text>
          </Box>
        </VStack>
      </Center>
    );
  }

  // Show inactive company message if company exists but is inactive
  if (company && isCompanyInactive) {
    return (
      <Center h={'100vh'}>
        <VStack spacing={4} textAlign='center' maxW='400px' px={4}>
          <Text fontSize='2xl' fontWeight='bold' color='red.500'>
            Servicio de reservas inactivo
          </Text>
          <Text color='gray.600' fontSize='lg'>
            El servicio de reservas de {company.name} se encuentra temporalmente inactivo con la
            plataforma Sontickets.
          </Text>

          <Box mt={8}>
            <Text fontSize={'sm'} textAlign={'center'}>
              Desarrollado por
              <Link isExternal href='https://sontickets.com/'>
                {' '}
                sontickets.com
              </Link>
            </Text>
          </Box>
        </VStack>
      </Center>
    );
  }

  return (
    <>
      {company && (
        <Helmet>
          {/* Google tag (gtag.js) */}
          <script
            async
            src={`https://www.googletagmanager.com/gtag/js?id=${conversionId}`}
          ></script>
          <script>
            {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${conversionId}');
          `}
          </script>

          {/* Event snippet for reservation conversion */}
          <script>
            {`
          function gtag_report_conversion(url) {
            gtag('event', 'conversion', {
              'send_to': '${conversionId}/${conversionTag}',
            });
            return false;
          }
          window.gtag_report_conversion = gtag_report_conversion;
          `}
          </script>

          {/* Meta Pixel Code */}
          <script>
            {`
!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window, document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init', '${metaPixelId}');
fbq('track', 'PageView');
            `}
          </script>
          <noscript>
            {`<img
              height='1'
              width='1'
              style={{ display: 'none' }}
              src='https://www.facebook.com/tr?id=${metaPixelId}&ev=PageView&noscript=1'
            />`}
          </noscript>
        </Helmet>
      )}
      <Form />

      <Text fontSize={'sm'} textAlign={'center'} mt={10}>
        Desarrolado por
        <Link isExternal href='https://sontickets.com/'>
          {' '}
          sontickets.com
        </Link>
      </Text>
    </>
  );
};
